<script setup lang="ts">
import { computed, ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import { toKebabCase } from '@/shared/utils/format'
import type { SizeType } from '@/shared/types/shared.types'

import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import SkeletonLoader from '@/shared/components/loader/SkeletonLoader.vue'
import LabelField from '@/shared/components/label/LabelField.vue'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue'

type Item = Record<string, unknown>

interface Props {
  title: string
  items: Item[]
  itemKey: string
  itemText: string
  size?: SizeType
  label?: boolean
  loading?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'change', value: unknown): void
}

const props = withDefaults(defineProps<Props>(), {
  label: false,
  size: 'sm',
  loading: false,
  disabled: false,
})

const emit = defineEmits<Emits>()

const model = defineModel<unknown>({ required: true })

const isOpen = ref(false)

const name = computed(() => toKebabCase(props.title))

const textSizes = computed(
  () =>
    ({
      xs: 'text-xs p-1',
      sm: 'text-sm p-2',
      md: 'text-base p-3',
      lg: 'text-lg p-4',
      xl: 'text-xl p-5',
    })[props.size],
)

const iconSizes = computed(
  () =>
    ({
      xs: 'h-4 w-4',
      sm: 'h-5 w-5',
      md: 'h-6 w-7',
      lg: 'h-7 w-7',
      xl: 'h-8 w-8',
    })[props.size],
)

const selectedItem = computed<Item | undefined>(() =>
  props.items.find((item) => item[props.itemKey] === model.value),
)

const displayText = computed(() => selectedItem.value?.[props.itemText])

const selectItem = (item: Item) => {
  model.value = item[props.itemKey]
  isOpen.value = false

  emit('change', model.value)
}

const onClose = (): void => {
  isOpen.value = false
}
</script>

<template>
  <div v-if="loading">
    <SkeletonLoader v-if="label" class="h-3.5 w-1/3 mb-2 mt-1.5" />
    <SkeletonLoader class="h-9.5 w-full" />
  </div>
  <div v-else class="relative" v-on-click-outside="onClose">
    <LabelField v-if="label" :id="name" :text="title" :disabled="disabled" />
    <button
      type="button"
      class="flex items-center text-start gap-1 w-full border bg-white dark:bg-black not-disabled:cursor-pointer disabled:bg-gray-100 disabled:text-gray-700"
      :class="[textSizes, disabled ? 'border-dashed border-gray-300' : 'border-gray-500']"
      :disabled="disabled"
      @click="isOpen = !isOpen"
    >
      <div v-if="selectedItem?.icon" class="shrink-0">
        <SvgIcon class="fill-gray-700" :class="iconSizes" :path="String(selectedItem.icon)" />
      </div>
      <span class="flex-1 truncate" :class="{ 'text-gray-400': !selectedItem }">
        {{ displayText }}
      </span>
      <CaretIcon :active="isOpen" class="shrink-0 h-5 w-5" :class="iconSizes" />
    </button>
    <FadeTransition>
      <div
        v-if="isOpen && items.length"
        class="absolute z-10 w-full max-h-64 border-x border-b border-gray-500 bg-white shadow-md overflow-auto"
      >
        <ul>
          <template v-for="(item, index) in items" :key="index">
            <li
              class="flex items-center gap-1 cursor-pointer hover:bg-gray-100"
              :class="textSizes"
              @click="selectItem(item)"
            >
              <SvgIcon
                v-if="item.icon"
                class="fill-gray-700 shrink-0"
                :class="iconSizes"
                :path="String(item.icon)"
              />
              <span class="truncate">
                {{ item[itemText] }}
              </span>
            </li>
          </template>
        </ul>
      </div>
    </FadeTransition>
  </div>
</template>
